import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Spinner } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiSensedia } from '../../../../../services/api-sensedia';
import * as CarteiraPrevidenciaService from '../../../../../services/previdencia-individual/carteira_previdencia.service';
import { SingleLgpdModal } from '../../components/carteira-previdencia/SingleLgpdModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { SearchFields } from '../components/SearchFields';
import { TableOffers } from '../components/TableOffers';
import { WarningTextBox } from '../components/WarningTextBox';
import { MovimentacaoFundosActions } from '../contexts/MovimentacaoFundosContext';
import { useMovimentacaoFundos } from '../hooks/useMovimentacaoFundos';
import { mockedMovimentacaoFundos } from '../mocks/mockedMovimentacaoFundos';
import { DataTableOffersType } from '../types/MovimentacaoFundosType';

const apiMf = apiSensedia;

const ROWS_PER_PAGE = 5;
const MOVIMENTACAO_FUNDOS_REQUEST_TIMEOUT_MS = Number(
  process.env.REACT_APP_MOVIMENTACAO_FUNDOS_REQUEST_TIMEOUT_MS || 5500
);
const MOVIMENTACAO_FUNDOS_POLLING_MAX_ATTEMPTS = Number(
  process.env.REACT_APP_MOVIMENTACAO_FUNDOS_POLLING_MAX_ATTEMPTS || 20
);
const MOVIMENTACAO_FUNDOS_POLLING_INTERVAL_MS = Number(
  process.env.REACT_APP_MOVIMENTACAO_FUNDOS_POLLING_INTERVAL_MS || 700
);

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const BaseClientes = () => {
  const { state, setState } = useMovimentacaoFundos();

  const navigate = useNavigate();

  const [dataTable, setDataTable] = useState<DataTableOffersType[]>();

  const [isLoading, setIsLoading] = useState(false);
  const [pollingInProgress, setPollingInProgress] = useState(false);

  const [clearFields, setClearFields] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showLGPDModal, setShowLGPDModal] = useState(false);

  const [consentimentoData, setConsentimentoData] = useState<{
    cpf: string;
    email?: string;
    celular?: string;
  }>({ cpf: '', email: '', celular: '' });

  const [totalDataTable, setTotalDataTable] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [propsRows, setPropsRows] = useState({
    offset: 0,
    limit: currentPage * ROWS_PER_PAGE,
    total: totalDataTable
  });

  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [cpfSearchTerm, setCpfSearchTerm] = useState('');
  const [propostaSearchTerm, setPropostaSearchTerm] = useState('');

  const totalPages = Math.ceil(totalDataTable / ROWS_PER_PAGE);

  const buildPropostasUrl = (
    limit: number,
    offset: number,
    name: string,
    cpf: string,
    offer: string
  ) => {
    return `/movimentacoes-fundos/propostas?_limit=${limit}&_offset=${offset}${
      name ? '&nome=' + name : ''
    }${cpf ? '&cpf=' + cpf : ''}${offer ? '&proposta=' + offer : ''}`;
  };

  const fetchPropostasWithPolling = async (
    limit: number,
    offset: number,
    name: string,
    cpf: string,
    offer: string
  ) => {
    setPollingInProgress(false);

    const requestUrl = buildPropostasUrl(limit, offset, name, cpf, offer);

    for (
      let attempt = 1;
      attempt <= MOVIMENTACAO_FUNDOS_POLLING_MAX_ATTEMPTS;
      attempt++
    ) {
      try {
        const response = await apiMf.get(requestUrl, {
          timeout: MOVIMENTACAO_FUNDOS_REQUEST_TIMEOUT_MS
        });

        const inProgress =
          response.status === 202 || response?.data?.status === 'in_progress';

        if (!inProgress) {
          setPollingInProgress(false);
          return response;
        }

        setPollingInProgress(true);

        const retryAfterMs = Number(
          response?.data?.retryAfterMs ||
            MOVIMENTACAO_FUNDOS_POLLING_INTERVAL_MS
        );

        if (attempt < MOVIMENTACAO_FUNDOS_POLLING_MAX_ATTEMPTS) {
          await wait(retryAfterMs);
        }
      } catch (error: any) {
        const status = error?.response?.status;

        if (
          status === 504 &&
          attempt < MOVIMENTACAO_FUNDOS_POLLING_MAX_ATTEMPTS
        ) {
          setPollingInProgress(true);
          await wait(MOVIMENTACAO_FUNDOS_POLLING_INTERVAL_MS);
          continue;
        }

        setPollingInProgress(false);
        throw error;
      }
    }

    setPollingInProgress(false);
    throw new Error('MOVIMENTACAO_FUNDOS_POLLING_TIMEOUT');
  };

  const getDataTable = async (
    limit: number,
    offset: number,
    name: string,
    cpf: string,
    offer: string
  ) => {
    setIsLoading(true);
    try {
      const res = await fetchPropostasWithPolling(
        limit,
        offset,
        name,
        cpf,
        offer
      );

      console.log('retorno propostas', res.data._embedded._result);

      setTotalDataTable(res.data._embedded._total);
      setDataTable(res.data._embedded._result);
      setPropsRows((prevState) => ({
        ...prevState,
        total: res.data._embedded._total
      }));
    } catch (e: any) {
      if (e?.response?.status === 500) {
        console.log('ocorreu internal server error', e.response.statusText);
        console.log('ocorreu internal server error', e.response.data);
      } else if (e?.response?.status === 504) {
        console.log('ocorreu gateway timeout', e.response.data);
      } else if (e?.message === 'MOVIMENTACAO_FUNDOS_POLLING_TIMEOUT') {
        console.log('consulta de movimentacao de fundos ainda em andamento');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const syncMdmData = () => {
    (async () => {
      const dataListFilterFields = {
        cpf: '',
        nome: '',
        produto: '',
        status: ''
      };

      let response: any;
      let error: any;
      try {
        response = await CarteiraPrevidenciaService.getData(
          1,
          dataListFilterFields
        );
      } catch (e: any) {
        console.log(e);
        error = e;
      } finally {
        console.log('sincronizou', error, response);
      }
    })();
  };

  const handleSelectOffers = (
    row: DataTableOffersType,
    consentStatus: string
  ) => {
    console.log('retorno consentimento', consentStatus);
    console.log('dados proposta', row);
    if (consentStatus === 'ALLOWED_BY_CLIENT') {
      console.log('caiu aqui 1');
      setState({
        type: MovimentacaoFundosActions.setCpf,
        payload: row.cpf
      });
      setState({
        type: MovimentacaoFundosActions.setData_nascimento,
        payload: row.data_nascimento
      });
      setState({
        type: MovimentacaoFundosActions.setEV,
        payload: Number(row.ev)
      });
      setState({
        type: MovimentacaoFundosActions.setId,
        payload: row.id
      });
      setState({
        type: MovimentacaoFundosActions.setId_contrato,
        payload: row.id_contrato
      });
      setState({
        type: MovimentacaoFundosActions.setNome_cliente,
        payload: row.nome_cliente
      });
      setState({
        type: MovimentacaoFundosActions.setNome_produto,
        payload: row.nome_produto
      });
      setState({
        type: MovimentacaoFundosActions.setNumero_proposta,
        payload: row.numero_proposta
      });
      setState({
        type: MovimentacaoFundosActions.setStatus,
        payload: row.status
      });
      setState({
        type: MovimentacaoFundosActions.setTelefone,
        payload: row.telefone_cliente
      });
      setState({
        type: MovimentacaoFundosActions.setValor_saldo,
        payload: row.valor_saldo
      });
      setState({
        type: MovimentacaoFundosActions.setProposta,
        payload: mockedMovimentacaoFundos
      });

      navigate(
        `/corretor/previdencia-individual/movimentacao-fundos/analitico-proposta`,
        {
          replace: true
        }
      );
    } else if (consentStatus === 'SENT') {
      setShowWarningModal(true);
    } else {
      setConsentimentoData({
        cpf: row.cpf.replace(/[^a-zA-Z0-9 ]/g, '').replaceAll(' ', ''),
        celular: row.telefone_cliente,
        email: row.email
      });

      setShowModal(true);
    }
  };

  const handlePageClick = (currentPage: number) => {
    // setClearFields(!clearFields);

    const offset = ROWS_PER_PAGE * currentPage - ROWS_PER_PAGE;
    const limit = ROWS_PER_PAGE;
    const paramBusca = {
      limit,
      offset,
      nameSearchTerm: nameSearchTerm || undefined,
      cpfSearchTerm: cpfSearchTerm || undefined,
      propostaSearchTerm: propostaSearchTerm || undefined
    };
    console.log('parametros busca', paramBusca);

    setCurrentPage(currentPage);
    setPropsRows({
      ...propsRows,
      offset: offset,
      limit: limit,
      total: totalDataTable
    });
    getDataTable(
      limit,
      offset,
      nameSearchTerm || undefined,
      cpfSearchTerm || undefined,
      propostaSearchTerm || undefined
    );
  };

  const dataPagination = {
    currentPage: currentPage,
    totalPages: totalPages,
    propsRows: propsRows,
    handlePageClick: handlePageClick
  };

  useEffect(() => {
    localStorage.removeItem('movimentacaoFundosState');
    console.log('limit e offset', propsRows.limit, propsRows.offset);
    getDataTable(5, 0, undefined, undefined, undefined);
    syncMdmData();
  }, []);

  const today = new Date();
  const dataInicio = new Date('2023-11-01T12:00:00.000-03:00');
  const dataFim = new Date('2023-11-01T22:00:00.000-03:00');

  return (
    <>
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-w-full tw-max-w-6xl tw-px-4 tw-sm:px-6 tw-lg:px-8 tw-py-6">
          <a
            onClick={() => navigate('/corretor')}
            className="tw-flex tw-items-center tw-cursor-pointer tw-text-blue-900 tw-font-bold"
          >
            <ChevronLeftIcon />
            <p>Voltar</p>
          </a>
        </div>

        <div className="tw-mb-3">
          {today <= dataFim ? (
            <WarningTextBox content="Devido a migração de bases do MDM as aplicações referentes ao LGPD estarão indisponíveis no dia 01/11/2023 de 12:00 às 22:00. Isso tornará o envio de solicitações de consentimento indisponível durante esse período." />
          ) : (
            <></>
          )}
        </div>
        <div className="tw-w-full tw-max-w-6xl tw-p-6 tw-bg-white tw-rounded-lg tw-shadow-md tw-mb-8">
          <h2 className="tw-text-lg tw-font-semibold tw-text-blue-900">
            Movimentação de fundos
          </h2>
          <hr className="tw-my-4 tw-border-gray-300" />
          <div className="tw-flex tw-items-center tw-space-x-4">
            <SearchFields
              clearFields={clearFields}
              setCurrentPage={(currentPage: number) => {
                setCurrentPage(currentPage);
              }}
              name={nameSearchTerm}
              setName={(name: string) => {
                setNameSearchTerm(name);
              }}
              cpf={cpfSearchTerm}
              setCpf={(cpf: string) => {
                setCpfSearchTerm(cpf);
              }}
              offer={propostaSearchTerm}
              setOffer={(proposta: string) => {
                setPropostaSearchTerm(proposta);
              }}
              handleSearchClient={getDataTable}
            />
          </div>
        </div>
        <div className="tw-w-full tw-max-w-6xl tw-p-6 tw-bg-white tw-rounded-lg tw-shadow-md tw-min-h-525">
          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-100 tw-gap-3">
              <Spinner size="xl" color="orange.500" />
              {pollingInProgress && (
                <p className="tw-text-sm tw-font-medium tw-text-blue-900">
                  Consulta em andamento. Atualizando os dados...
                </p>
              )}
            </div>
          ) : (
            <TableOffers
              dataTable={dataTable}
              dataPagination={dataPagination}
              handleSelectOffers={handleSelectOffers}
            />
          )}
        </div>
      </div>
      {showModal ? (
        <ConfirmationModal
          title="Consentimento"
          content="Não existe consentimento do cliente para esse tipo de movimentação"
          afirmativeButtonText="Solicitar consentimento"
          negativeButtonText="Cancelar"
          handleModalConfirmation={(action: string) => {
            if (action === 'close-modal') {
              setShowModal(false);
            }
            if (action === 'submit') {
              //abre página do consentimento com target _blank
              /*window.open(
                '/corretor/previdencia-individual/carteira-previdencia',
                '_blank'
              );*/

              setShowModal(false);
              // Exibe o modal LGPD
              setShowLGPDModal(true);
              console.log('dados consentimento aqui', consentimentoData);
            }
          }}
        />
      ) : (
        ''
      )}
      {showWarningModal ? (
        <ConfirmationModal
          title="Consentimento Pendente"
          content="O consentimento foi solicitado. Aguardando retorno do cliente."
          afirmativeButtonText=""
          negativeButtonText=""
          handleModalConfirmation={(action: string) => {
            if (action === 'close-modal') {
              setShowWarningModal(false);
            }
            if (action === 'submit') {
              setShowWarningModal(false);
            }
          }}
        />
      ) : (
        ''
      )}
      <SingleLgpdModal
        isOpen={showLGPDModal}
        onClose={() => {
          setShowLGPDModal(false);
        }}
        data={consentimentoData}
        onSuccessRequest={() => {
          //
        }}
      />
    </>
  );
};
